"use client";

import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Heart, Download, User, Calendar, Share2 } from "lucide-react";
import { RewardedDownloadButton } from "@/components/rewarded-download-button";
import { getMediaUrl } from "@/lib/api";
import { generateGifUrl, generateGifTagUrl } from "@/lib/utils";
import { formatDistanceToNowStrict } from "date-fns";

interface GifCardProps {
  gif: any;
  viewMode?: "grid" | "list";
  onLike?: (gif: any) => void;
  onDownload?: (gif: any) => void;
  onShare?: (gif: any) => void;
}

export default function GifCard({
  gif,
  viewMode = "grid",
  onLike,
  onDownload,
  onShare,
}: GifCardProps) {
  if (!gif) return null;

  const detailUrl = generateGifUrl(gif.id, gif.title);
  const imageUrl = getMediaUrl(
    gif.gif_path ?? (gif.gif_url ? gif.gif_url.replace("/media/", "") : null)
  );

  if (viewMode === "grid") {
    return (
      <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all duration-200 group overflow-hidden flex flex-col h-full">
        <CardContent className="p-0 flex flex-col h-full">
          <Link href={detailUrl} className="block flex-1 group">
            {/* Thumbnail */}
            <div className="relative aspect-square overflow-hidden rounded-t-lg bg-zinc-950">
              <img
                src={imageUrl}
                alt={gif.title || ""}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
            </div>

            {/* Info */}
            <div className="p-4">
              <h3 className="font-semibold text-white mb-2 line-clamp-2 group-hover:text-red-500 transition-colors">
                {gif.title}
              </h3>
              <div className="flex items-center justify-between text-sm text-zinc-400 mb-2">
                <span className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {gif.no_of_views ?? 0}
                </span>
                <span className="flex items-center gap-1">
                  <Heart className="h-3 w-3" />
                  {gif.likes ?? 0}
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(gif.category?.name || gif.category_name || gif.categoryname || (typeof gif.category === 'string' ? gif.category : null)) && (
                  <Badge variant="secondary" className="bg-zinc-800 text-zinc-300 text-xs">
                    {gif.category?.name || gif.category_name || gif.categoryname || gif.category}
                  </Badge>
                )}
              </div>
            </div>
          </Link>
        </CardContent>
      </Card>
    );
  }

  // List View
  return (
    <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all duration-200 group overflow-hidden">
      <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1 min-w-0 w-full">
          {/* Thumbnail wrapper Link */}
          <Link
            href={detailUrl}
            className="block relative w-24 h-24 overflow-hidden rounded-lg flex-shrink-0"
          >
            <img
              src={imageUrl}
              alt={gif.title || ""}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          </Link>

          {/* Info wrapper */}
          <div className="flex-1 min-w-0 w-full">
            <Link href={detailUrl} className="block group">
              <h3 className="font-semibold text-white mb-2 group-hover:text-red-500 transition-colors">
                {gif.title}
              </h3>
            </Link>
            {gif.description && (
              <p className="text-zinc-400 text-sm mb-2 line-clamp-2">
                {gif.description}
              </p>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-2">
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {gif.user?.username || "Unknown"}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {gif.created_at
                  ? formatDistanceToNowStrict(new Date(gif.created_at), {
                      addSuffix: true,
                    })
                  : ""}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {gif.no_of_views ?? 0}
              </span>
              <span className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                {gif.likes ?? 0}
              </span>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {(gif.category?.name || gif.category_name || gif.categoryname || (typeof gif.category === 'string' ? gif.category : null)) && (
                <Badge variant="secondary" className="bg-zinc-800 text-zinc-300 text-xs">
                  {gif.category?.name || gif.category_name || gif.categoryname || gif.category}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* CTAs on the Right */}
        {(onLike || onDownload || onShare) && (
          <div className="flex gap-2 self-end sm:self-center">
            {onLike && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onLike(gif);
                }}
                className="text-zinc-400 hover:text-red-400"
              >
                <Heart className="h-4 w-4" />
              </Button>
            )}
            {onDownload && (
              <RewardedDownloadButton
                uiTargetId={gif?.id ? `gif-${gif.id}` : undefined}
                size="sm"
                variant="ghost"
                iconOnly
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDownload(gif);
                }}
                className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400"
                aria-label="Download GIF"
              >
                <Download className="h-4 w-4" />
              </RewardedDownloadButton>
            )}
            {onShare && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShare(gif);
                }}
                className="text-zinc-400 hover:text-green-400"
              >
                <Share2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
